import React from 'react'
import { useAuth } from '@/context/AuthProvider'

type MetricEvent = { name: string; value: number; rating?: string; id?: string }
type Line = { ts: string; text: string }

export const DebugPanel: React.FC = () => {
	const [open, setOpen] = React.useState(false)
	const [metrics, setMetrics] = React.useState<Line[]>([])
	const [syncLines, setSyncLines] = React.useState<Line[]>([])
	const endRef1 = React.useRef<HTMLDivElement>(null)
	const endRef2 = React.useRef<HTMLDivElement>(null)
	const { user, loading } = useAuth()
	// Dock state: posiciones predefinidas
	type Dock = 'tl' | 'tr' | 'bl' | 'br' | 'rc'
	const [dock, setDock] = React.useState<Dock>(() => {
		try { return (sessionStorage.getItem('wrlens_debug_dock') as Dock) || 'tl' } catch { return 'tl' }
	})
	React.useEffect(() => { try { sessionStorage.setItem('wrlens_debug_dock', dock) } catch {} }, [dock])
	const panelRef = React.useRef<HTMLDivElement>(null)
	const handleRef = React.useRef<HTMLDivElement>(null)

	React.useEffect(() => {
		if (process.env.NODE_ENV !== 'development') return
		const onMetric = (e: Event) => {
			const d = (e as CustomEvent).detail as MetricEvent
			const val = d.name === 'CLS' ? d.value.toFixed(3) : (d.name === 'LCP' || d.name === 'FCP') ? (d.value/1000).toFixed(2)+'s' : Math.round(d.value)+'ms'
			const txt = d.rating ? `${d.name}=${val} (rating=${d.rating})` : `${d.name}=${val}`
			setMetrics(prev => [...prev, { ts: new Date().toLocaleTimeString(), text: txt }].slice(-200))
			setOpen(true)
		}
		const onSync = (e: Event) => {
			const d = (e as CustomEvent).detail || {}
			const s = d.stage as string | undefined
			if (!s) return
			let line = d.trigger ? `[${d.trigger}] ${s}` : s
			if (d.count) line += ` (${d.count})`
			if (d.batch) line += ` (+${d.batch}, total ${d.total})`
			setSyncLines(prev => [...prev, { ts: new Date().toLocaleTimeString(), text: line }].slice(-200))
			setOpen(true)
		}
		// Cargar buffer global de métricas si existe
		try {
			const w: any = window as any
			const buf: any[] | undefined = w.__wrlens_metrics
			if (Array.isArray(buf) && buf.length) {
				const formatted = buf.map((d: any) => {
					const val = d.name === 'CLS' ? d.value.toFixed(3) : (d.name === 'LCP' || d.name === 'FCP') ? (d.value/1000).toFixed(2)+'s' : Math.round(d.value)+'ms'
					const txt = d.rating ? `${d.name}=${val} (rating=${d.rating})` : `${d.name}=${val}`
					return { ts: new Date(d.ts || Date.now()).toLocaleTimeString(), text: txt }
				})
				setMetrics(prev => [...prev, ...formatted].slice(-200))
			}
		} catch {}
		window.addEventListener('wrlens:metrics', onMetric)
		const onToggle = () => setOpen(o => !o)
		const onOpen = () => setOpen(true)
		const onClose = () => setOpen(false)
		window.addEventListener('wrlens:debug-toggle', onToggle)
		window.addEventListener('wrlens:debug-open', onOpen)
		window.addEventListener('wrlens:debug-close', onClose)
		window.addEventListener('wrlens:sync-progress', onSync)
		return () => {
			window.removeEventListener('wrlens:metrics', onMetric)
			window.removeEventListener('wrlens:debug-toggle', onToggle)
			window.removeEventListener('wrlens:debug-open', onOpen)
			window.removeEventListener('wrlens:debug-close', onClose)
			window.removeEventListener('wrlens:sync-progress', onSync)
		}
	}, [])

	React.useEffect(() => { endRef1.current?.scrollIntoView({ behavior: 'smooth' }) }, [metrics])
	React.useEffect(() => { endRef2.current?.scrollIntoView({ behavior: 'smooth' }) }, [syncLines])

	if (process.env.NODE_ENV !== 'development') return null

	if (!open) return null

	const dockStyle = (() => {
		const base: React.CSSProperties = { position: 'fixed', zIndex: 50 }
		const gap = 16
		switch (dock) {
			case 'tl': return { ...base, left: gap, top: gap }
			case 'tr': return { ...base, right: gap, top: gap }
			case 'bl': return { ...base, left: gap, bottom: gap }
			case 'br': return { ...base, right: gap, bottom: gap }
			case 'rc': return { ...base, right: gap, top: '50%', transform: 'translateY(-50%)' }
			default: return { ...base, left: gap, top: gap }
		}
	})()

	return (
		<div style={dockStyle}>
			{open && (
				<div ref={panelRef} className="mt-2 w-[520px] max-h-[55vh] bg-black/85 border border-white/20 rounded-lg shadow-2xl backdrop-blur text-xs grid grid-cols-2 gap-3">
					<div ref={handleRef} className="col-span-2 flex items-center justify-between text-[#F8F6EF] font-semibold px-3 py-2 select-none border-b border-white/10">
						<span>Debug</span>
						<div className="flex items-center gap-1">
							<button className="px-2 py-0.5 text-[11px] rounded bg-gray-700 hover:bg-gray-600" title="Esquina sup. izda" onClick={() => setDock('tl')}>↖</button>
							<button className="px-2 py-0.5 text-[11px] rounded bg-gray-700 hover:bg-gray-600" title="Esquina sup. dcha" onClick={() => setDock('tr')}>↗</button>
							<button className="px-2 py-0.5 text-[11px] rounded bg-gray-700 hover:bg-gray-600" title="Derecha centro" onClick={() => setDock('rc')}>⇔</button>
							<button className="px-2 py-0.5 text-[11px] rounded bg-gray-700 hover:bg-gray-600" title="Esquina inf. izda" onClick={() => setDock('bl')}>↙</button>
							<button className="px-2 py-0.5 text-[11px] rounded bg-gray-700 hover:bg-gray-600" title="Esquina inf. dcha" onClick={() => setDock('br')}>↘</button>
							<button
								type="button"
								className="ml-2 px-2 py-0.5 text-[11px] rounded bg-gray-700 hover:bg-gray-600"
								onClick={() => setOpen(false)}
							>Ocultar</button>
						</div>
					</div>
					<div className="col-span-2 p-3 grid grid-cols-2 gap-3">
					<div className="space-y-2">
						<div className="text-[#ebd4b5] font-medium">Metrics</div>
						<div className="h-40 overflow-auto space-y-1 pr-1">
							{metrics.map((m, i) => (
								<div key={i} className="text-[#ebd4b5]"><span className="opacity-60">[{m.ts}]</span> {m.text}</div>
							))}
							<div ref={endRef1} />
						</div>
					</div>
					<div className="space-y-2">
						<div className="text-[#ebd4b5] font-medium">Sync</div>
						<div className="h-40 overflow-auto space-y-1 pr-1">
							{syncLines.map((l, i) => (
								<div key={i} className="text-[#ebd4b5]"><span className="opacity-60">[{l.ts}]</span> {l.text}</div>
							))}
							<div ref={endRef2} />
						</div>
					</div>
					<div className="col-span-2 text-[#ebd4b5]">
						<div className="font-medium mb-1">Auth</div>
						<div className="grid grid-cols-2 gap-x-4 gap-y-1">
							<div><span className="opacity-70">loading:</span> {loading ? 'true' : 'false'}</div>
							<div><span className="opacity-70">user:</span> {user ? 'authenticated' : 'not authenticated'}</div>
							{user && <>
								<div><span className="opacity-70">id:</span> {user.id}</div>
								<div><span className="opacity-70">email:</span> {user.email}</div>
								<div className="col-span-2"><span className="opacity-70">created:</span> {user.created_at}</div>
							</>}
						</div>
					</div>
					</div>
				</div>
			)}
		</div>
	)
}

export default DebugPanel
